import { useEffect, useState } from '@wordpress/element';
import { initCopy } from '../../../resources/scripts/module/utils';
import { generateVimeoIframe } from '../utils/methods';

const { useSelect } = wp.data;
const { registerBlockType } = wp.blocks;
const {
  RichText,
  InnerBlocks,
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  URLInputButton,
} = wp.blockEditor;
const { Button, TextControl, PanelBody } = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

registerBlockType('pg/podcast-content-post', {
  title: __('PG Podcast Content Block'),
  icon: 'microphone',
  category: 'layout',
  attributes: {
    title: { type: 'string' },
    excerpt: { type: 'string' },
    mainPhoto: { type: 'string', default: '' },
    mainPhotoAlt: { type: 'string', default: '' }, // Add alt attribute
    vimeoUrl: { type: 'string', default: '' },
    subscribeLinks: {
      type: 'array',
      default: [
        { id: 1, name: 'Spotify', url: '#' },
        { id: 2, name: 'Apple Podcasts', url: '#' },
      ],
      source: 'query',
      selector: '.subscribe-links .link-item',
      query: {
        id: { source: 'attribute', attribute: 'data-id' },
        name: { source: 'text', query: 'a' },
        url: { source: 'attribute', attribute: 'href', query: 'a' },
      },
    },
    subscribeLinksJson: { type: 'string', default: '[]' },
    subscribeLinksTitle: {
      type: 'string',
      default: 'Subscribe to the podcast',
    },
    greenBoxHeader: { type: 'string' },
    greenBoxContent: { type: 'string' },
    greenBoxShareTitle: { type: 'string', default: 'Share Episode' },
  },

  edit: ({ attributes, setAttributes }) => {
    const {
      title,
      excerpt,
      subscribeLinks,
      subscribeLinksJson,
      subscribeLinksTitle,
      mainPhoto,
      mainPhotoAlt, // Use alt attribute
      vimeoUrl,
    } = attributes;
    const currentPost = useSelect(
      (select) => select('core/editor').getCurrentPost(),
      null
    );
    const postLink = currentPost && currentPost.link;
    const [loaded, setLoaded] = useState(false);
    attributes.subscribeLinks = subscribeLinksJson
      ? JSON.parse(subscribeLinksJson)
      : [];

    useEffect(() => {
      initCopy();
      setLoaded(true);
    }, [loaded]);

    const handleAddLink = () => {
      const newLink = {
        id: Math.max(0, ...subscribeLinks.map((link) => link.id)) + 1,
        name: '',
        url: '',
      };
      setAttributes({ subscribeLinks: [...subscribeLinks, newLink] });
      setAttributes({
        subscribeLinksJson: JSON.stringify([...subscribeLinks, newLink]),
      });
    };

    const handleRemoveLink = (id) => {
      const newLinks = subscribeLinks.filter((link) => link.id !== id);
      setAttributes({ subscribeLinks: newLinks });
      setAttributes({ subscribeLinksJson: JSON.stringify(newLinks) });
    };

    const handleUpdateLink = (id, field, value) => {
      const newLinks = subscribeLinks.map((link) => {
        if (link.id === id) {
          return { ...link, [field]: value }; // Ensure a new object is created
        }
        return link;
      });
      setAttributes({ subscribeLinks: newLinks });
      setAttributes({ subscribeLinksJson: JSON.stringify(newLinks) });
    };

    const handleVimeoUrlChange = (url) => {
      const iframe = generateVimeoIframe(url);
      setAttributes({ vimeoUrl: url });
      if (iframe) {
        setAttributes({ mainPhoto: '' });
      }
    };

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Media Settings')}>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) =>
                  setAttributes({
                    mainPhoto: media.url,
                    mainPhotoAlt: media.alt || '', // Capture alt attribute
                  })
                }
                allowedTypes={['image']}
                value={mainPhoto}
                render={({ open }) => (
                  <Button isPrimary onClick={open}>
                    {__('Set Main Image')}
                  </Button>
                )}
              />
            </MediaUploadCheck>
            <TextControl
              label={__('Vimeo URL')}
              value={vimeoUrl}
              onChange={handleVimeoUrlChange}
              placeholder={__('Enter Vimeo URL')}
            />
          </PanelBody>
        </InspectorControls>
        <div className="editor-component">
          <section className="section-light-primary">
            <article className="post post-podcast">
              <header>
                <RichText
                  tagName="h2"
                  className="title"
                  placeholder={__('Title')}
                  value={title}
                  onChange={(value) => setAttributes({ title: value })}
                />
                <RichText
                  tagName="div"
                  className="excerpt"
                  placeholder={__('Excerpt')}
                  value={excerpt}
                  onChange={(value) => setAttributes({ excerpt: value })}
                />
              </header>
              <div className="content">
                <div className="subscribe-links" data-json={subscribeLinksJson}>
                  <RichText
                    tagName="span"
                    placeholder={__('Title')}
                    value={subscribeLinksTitle}
                    onChange={(value) =>
                      setAttributes({ subscribeLinksTitle: value })
                    }
                  />
                  {loaded &&
                    subscribeLinks.map((link) => (
                      <div
                        className={'link-item'}
                        data-id={link.id}
                        key={link.id}
                        style={{ marginBottom: '10px' }}
                      >
                        <RichText
                          tagName="a"
                          value={link.name}
                          placeholder={__('Link name')}
                          onChange={(value) =>
                            handleUpdateLink(link.id, 'name', value)
                          }
                          keepPlaceholderOnFocus
                        />
                        <URLInputButton
                          url={link.url}
                          onChange={(url) =>
                            handleUpdateLink(link.id, 'url', url)
                          }
                        />
                        <Button
                          isSmall
                          isDestructive
                          onClick={() => handleRemoveLink(link.id)}
                        >
                          {__('Remove')}
                        </Button>
                      </div>
                    ))}
                  <Button isDefault onClick={handleAddLink}>
                    {__('Add New Link')}
                  </Button>
                </div>
                <div className="video-block">
                  {vimeoUrl ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: generateVimeoIframe(vimeoUrl),
                      }}
                    />
                  ) : (
                    mainPhoto && (
                      <img
                        src={mainPhoto}
                        alt={mainPhotoAlt || ''} // Render alt attribute
                        className={'content-image'}
                      />
                    )
                  )}
                </div>
                <div className="column-block">
                  <div className="left">
                    <InnerBlocks
                      allowedBlocks={[
                        'core/paragraph',
                        'core/image',
                        'core/heading',
                        'core/list',
                        'core/embed',
                        'core/table',
                        'core/freeform',
                        'core/quote',
                        'pg/quote',
                        'pg/button',
                        'pg/banner',
                        'pg/list',
                      ]}
                      template={[
                        [
                          'core/paragraph',
                          {
                            placeholder:
                              'Start writing or type / to choose a block',
                          },
                        ],
                        ['core/image'],
                      ]}
                    />
                  </div>
                  <div className="right">
                    <div className="green-box">
                      <RichText
                        tagName="h4"
                        placeholder={__('Header text...')}
                        value={attributes.greenBoxHeader}
                        onChange={(value) =>
                          setAttributes({ greenBoxHeader: value })
                        }
                      />
                      <RichText
                        tagName="p"
                        placeholder={__('Content text...')}
                        value={attributes.greenBoxContent}
                        onChange={(value) =>
                          setAttributes({ greenBoxContent: value })
                        }
                      />
                      <div className="share">
                        <RichText
                          tagName="h6"
                          placeholder={__('Share header...')}
                          value={attributes.greenBoxShareTitle}
                          onChange={(value) =>
                            setAttributes({ greenBoxShareTitle: value })
                          }
                        />
                        <p>
                          <a href={postLink} className="share-copy copy-link">
                            Copy link
                          </a>
                          <a
                            href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                              postLink
                            )}`}
                            className="share-linkedin"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Linkedin
                          </a>
                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                              postLink
                            )}`}
                            className="share-facebook"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Facebook
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </section>
        </div>
      </Fragment>
    );
  },

  save: ({ attributes }) => {
    const {
      title,
      excerpt,
      subscribeLinksTitle,
      subscribeLinksJson,
      mainPhoto,
      mainPhotoAlt, // Render alt attribute
      vimeoUrl,
    } = attributes;
    return (
      <div className="editor-component">
        <section className="section-light-primary">
          <article className="post post-podcast">
            <header>
              <h1 className="title">{title}</h1>
              <div className="excerpt">{excerpt}</div>
            </header>
            <div className="content">
              <div className="subscribe-links" data-json={subscribeLinksJson}>
                <span>{subscribeLinksTitle}</span>
              </div>
              <div className="video-block">
                {vimeoUrl ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: generateVimeoIframe(vimeoUrl),
                    }}
                  />
                ) : (
                  mainPhoto && (
                    <img
                      src={mainPhoto}
                      alt={mainPhotoAlt || ''} // Render alt attribute
                      className={'content-image'}
                    />
                  )
                )}
              </div>
              <div className="column-block">
                <div className="left">
                  <InnerBlocks.Content />
                </div>
                <div className="right">
                  <div className="green-box">
                    <h4>{attributes.greenBoxHeader}</h4>
                    <p>{attributes.greenBoxContent}</p>
                    <div className="share">
                      <h6>{attributes.greenBoxShareTitle}</h6>
                      <p>
                        <a href="#" className="share-copy">
                          Copy link
                        </a>
                        <a href="#" className="share-linkedin">
                          Linkedin
                        </a>
                        <a href="#" className="share-facebook">
                          Facebook
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </section>
      </div>
    );
  },
});
